// DRAFT / SCAFFOLD — not published (excluded from hub + sitemap via `draft: true`).
//
// This is the flagship original-data study: the single best link/citation magnet
// for the domain. It is intentionally NOT live because it must be populated with
// REAL aggregate numbers mined from the Listino listing-prep corpus — fabricating
// figures would destroy the E-E-A-T this page exists to build.
//
// TO PUBLISH: run the corpus analysis, replace every `[[FILL: ...]]` placeholder
// with a real, dated figure, add an `Article + Dataset` chart/image, remove
// `draft: true`, and add the URL to root/sitemap.xml. See docs/SEO_GEO.md.

/** @param {string} what @returns {string} */
const FILL = (what) => `[[FILL: ${what}]]`;

const article = {
  slug: "listing-quality-data-study",
  cluster: "data",
  draft: true,
  title: "What We Learned Analyzing Thousands of Listings (2026)",
  description:
    "Original Listino data on what separates listings that sell from listings that sit — photos, descriptions, pricing, and days on market.",
  audience: "Sellers & agents",
  funnelStage: "TOFU",
  datePublished: "2026-06-25",
  dateModified: "2026-06-25",
  readingTime: "9 min read",
  tldr: "We analyzed the listings run through Listino to find what actually separates homes that sell quickly from homes that sit. This is the original-data flagship — the numbers below are placeholders until the corpus analysis is finalized.",
  blocks: [
    {
      type: "callout",
      spans: [
        "Editor's note: this study is in preparation. The figures below are placeholders and will be replaced with real, dated numbers from our analyzed-listings corpus before publication.",
      ],
    },
    {
      type: "h2",
      id: "what-we-studied",
      text: "What we studied",
    },
    {
      type: "p",
      spans: [
        "We looked at the listings analyzed on Listino and compared the ones that performed well against the ones that struggled, across four levers we examine in every listing prep: photo quality and order, listing-description strength, price position versus comparable sales, and days on market.",
      ],
    },
    {
      type: "h2",
      id: "headline-findings",
      text: "Headline findings",
    },
    {
      type: "stat",
      value: FILL("share of listings with a weak lead photo"),
      label: "of analyzed listings led with a photo our model flagged as weak",
    },
    {
      type: "stat",
      value: FILL("share of listings priced above sold-comp range"),
      label: "were priced above the range of recent comparable sales",
    },
    {
      type: "stat",
      value: FILL("description-length / specificity finding"),
      label: "of descriptions led with generic adjectives instead of a standout feature",
    },
    {
      type: "h2",
      id: "photos",
      text: "Photos: order matters as much as quality",
    },
    {
      type: "p",
      spans: [
        FILL("qualitative + quantified finding on photo order vs engagement"),
        " (placeholder). For the practical version, see ",
        {
          text: "the best order for listing photos",
          href: "/resources/best-order-for-listing-photos",
        },
        ".",
      ],
    },
    {
      type: "h2",
      id: "pricing",
      text: "Pricing: the most common and most expensive mistake",
    },
    {
      type: "p",
      spans: [
        FILL("finding on price position vs days on market"),
        " (placeholder). See ",
        { text: "is my house overpriced", href: "/resources/is-my-house-overpriced" },
        ".",
      ],
    },
    {
      type: "h2",
      id: "methodology",
      text: "Methodology",
    },
    {
      type: "p",
      spans: [
        "How we collected and analyzed this data, and how we handle statistics, is documented on our ",
        { text: "methodology page", href: "/resources/methodology" },
        ".",
      ],
    },
  ],
  related: ["why-isnt-my-house-selling", "is-my-house-overpriced", "best-order-for-listing-photos"],
};

export default article;
